import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Input, Button, Space, Typography, Tooltip, Upload } from 'antd';
import {
    SendOutlined, LoadingOutlined, RobotOutlined, DeleteOutlined,
    PaperClipOutlined, CloseOutlined, UpOutlined, DownOutlined,
    UserOutlined
} from '@ant-design/icons';
import api from '../../../api/client';

const { Text } = Typography;
const { TextArea } = Input;

const GeminiChatPanel = ({ canvasId, collapsed, onToggleCollapse }) => {
    const [messages, setMessages] = useState([]); // { role: 'user'|'model', parts: [...], pending?: bool }
    const [input, setInput] = useState('');
    const [streaming, setStreaming] = useState(false);
    const [attachedImage, setAttachedImage] = useState(null); // { base64, mimeType, preview }
    const chatEndRef = useRef(null);
    const fileInputRef = useRef(null);

    // Auto-scroll to bottom when messages change
    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleImageAttach = useCallback((file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const base64Full = e.target.result;
            const base64Data = base64Full.split(',')[1]; // Remove data:...;base64, prefix
            const mimeType = file.type || 'image/jpeg';
            setAttachedImage({
                base64: base64Data,
                mimeType,
                preview: base64Full,
                name: file.name
            });
        };
        reader.readAsDataURL(file);
        return false; // Prevent default upload
    }, []);

    const handleSend = useCallback(async () => {
        const trimmed = input.trim();
        if (!trimmed && !attachedImage) return;
        if (streaming) return;

        // Build user message parts
        const parts = [];
        if (attachedImage) {
            parts.push({
                inlineData: {
                    mimeType: attachedImage.mimeType,
                    data: attachedImage.base64
                }
            });
        }
        if (trimmed) {
            parts.push({ text: trimmed });
        }

        const userMessage = { role: 'user', parts };
        // Store preview info for rendering
        userMessage._preview = attachedImage ? attachedImage.preview : null;
        userMessage._text = trimmed;

        const newMessages = [...messages, userMessage];
        setMessages(newMessages);
        setInput('');
        setAttachedImage(null);
        setStreaming(true);

        // Add a pending assistant message
        const assistantMessage = { role: 'model', parts: [{ text: '' }], _text: '', pending: true };
        setMessages([...newMessages, assistantMessage]);

        try {
            // Build contents array for the API (strip internal fields)
            const contents = [...newMessages].map(m => ({
                role: m.role,
                parts: m.parts.map(p => {
                    if (p.inlineData) return { inlineData: p.inlineData };
                    if (p.text !== undefined) return { text: p.text };
                    return p;
                })
            }));

            // Get the base URL and auth token (same way as api client interceptor)
            const baseURL = api.defaults.baseURL || '';
            let token = null;
            try {
                const { auth } = await import('../../../config/firebase');
                if (auth.currentUser) {
                    token = await auth.currentUser.getIdToken();
                }
            } catch (e) {
                console.error('Error getting auth token:', e);
            }

            const response = await fetch(`${baseURL}/gemini-chat/stream`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                },
                body: JSON.stringify({ messages: contents })
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let fullText = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value, { stream: true });
                const lines = chunk.split('\n');

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        const data = line.slice(6);
                        if (data === '[DONE]') continue;

                        try {
                            const parsed = JSON.parse(data);
                            if (parsed.error) {
                                fullText += `\n[Error: ${parsed.error}]`;
                            } else if (parsed.text) {
                                fullText += parsed.text;
                            }
                        } catch {
                            // Skip malformed JSON
                        }
                    }
                }

                // Update the assistant message in place
                setMessages(prev => {
                    const updated = [...prev];
                    const last = updated[updated.length - 1];
                    if (last && last.role === 'model') {
                        last.parts = [{ text: fullText }];
                        last._text = fullText;
                        last.pending = false;
                    }
                    return [...updated];
                });
            }
        } catch (error) {
            console.error('Gemini chat error:', error);
            setMessages(prev => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last && last.role === 'model') {
                    last.parts = [{ text: `Error: ${error.message}` }];
                    last._text = `Error: ${error.message}`;
                    last.pending = false;
                }
                return [...updated];
            });
        } finally {
            setStreaming(false);
        }
    }, [input, attachedImage, messages, streaming]);

    const handleClear = useCallback(() => {
        setMessages([]);
        setInput('');
        setAttachedImage(null);
    }, []);

    return (
        <div style={{
            position: 'relative',
            height: collapsed ? 'auto' : '100%',
        }}>
            {/* Floating Toggle Button */}
            <div style={{
                position: 'absolute',
                top: collapsed ? 10 : 'auto',
                bottom: collapsed ? 'auto' : 8,
                right: 8,
                zIndex: 10
            }}>
                <Button
                    type="text"
                    icon={collapsed ? <UpOutlined /> : <DownOutlined />}
                    size="small"
                    onClick={onToggleCollapse}
                    style={{
                        color: '#8b949e',
                        background: 'rgba(22, 27, 34, 0.95)',
                        border: '1px solid #30363d',
                        borderRadius: 6
                    }}
                />
            </div>

            {/* Main Panel Card */}
            <div style={{
                background: 'rgba(22, 27, 34, 0.95)',
                border: '1px solid #30363d',
                borderRadius: 12,
                display: 'flex',
                flexDirection: 'column',
                height: collapsed ? 'auto' : '100%',
                overflow: 'hidden',
                backdropFilter: 'blur(8px)',
                position: 'relative',
                zIndex: 2
            }}>
                {/* Header */}
                <div style={{
                    padding: '12px 16px',
                    borderBottom: collapsed ? 'none' : '1px solid #30363d',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: 'rgba(255,255,255,0.02)'
                }}>
                    <Space>
                        <RobotOutlined style={{ color: '#4285f4' }} />
                        <Text style={{ color: '#fff', fontWeight: 600 }}>Gemini</Text>
                    </Space>
                    {!collapsed && messages.length > 0 && (
                        <Tooltip title="Clear chat">
                            <Button
                                type="text"
                                icon={<DeleteOutlined />}
                                size="small"
                                onClick={handleClear}
                                style={{ color: '#8b949e' }}
                            />
                        </Tooltip>
                    )}
                </div>

                {!collapsed && (
                    <>
                        {/* Chat Messages Area */}
                        <div
                            className="gemini-chat-messages"
                            style={{
                                flex: 1,
                                overflowY: 'auto',
                                padding: 12,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 10,
                                minHeight: 200,
                            }}
                        >
                            <style>{`
                                .gemini-chat-messages::-webkit-scrollbar { width: 6px; }
                                .gemini-chat-messages::-webkit-scrollbar-track { background: transparent; }
                                .gemini-chat-messages::-webkit-scrollbar-thumb { background: #30363d; border-radius: 3px; }
                            `}</style>

                            {messages.length === 0 && (
                                <div style={{
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#484f58',
                                    gap: 12,
                                    padding: 40
                                }}>
                                    <RobotOutlined style={{ fontSize: 36, opacity: 0.4 }} />
                                    <Text style={{ color: '#484f58', textAlign: 'center', fontSize: 13 }}>
                                        Ask Gemini anything — text, images, or both.
                                    </Text>
                                </div>
                            )}

                            {messages.map((msg, idx) => (
                                <div
                                    key={idx}
                                    style={{
                                        display: 'flex',
                                        justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                                        alignItems: 'flex-start',
                                        gap: 8
                                    }}
                                >
                                    {msg.role === 'model' && (
                                        <div style={{
                                            width: 24,
                                            height: 24,
                                            borderRadius: '50%',
                                            background: 'rgba(66, 133, 244, 0.15)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                            marginTop: 2
                                        }}>
                                            <RobotOutlined style={{ fontSize: 12, color: '#4285f4' }} />
                                        </div>
                                    )}

                                    <div style={{
                                        maxWidth: '85%',
                                        padding: '8px 12px',
                                        borderRadius: msg.role === 'user' ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                                        background: msg.role === 'user'
                                            ? 'rgba(66, 133, 244, 0.2)'
                                            : 'rgba(255, 255, 255, 0.06)',
                                        border: msg.role === 'user'
                                            ? '1px solid rgba(66, 133, 244, 0.3)'
                                            : '1px solid rgba(255, 255, 255, 0.08)',
                                    }}>
                                        {/* Image preview in user message */}
                                        {msg._preview && (
                                            <img
                                                src={msg._preview}
                                                alt="attached"
                                                style={{
                                                    maxWidth: '100%',
                                                    maxHeight: 150,
                                                    borderRadius: 8,
                                                    marginBottom: msg._text ? 8 : 0,
                                                    objectFit: 'cover'
                                                }}
                                            />
                                        )}

                                        {/* Text content */}
                                        {(msg._text || msg.parts?.[0]?.text) && (
                                            <Text style={{
                                                color: '#e6edf3',
                                                fontSize: 13,
                                                lineHeight: 1.5,
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word',
                                                display: 'block'
                                            }}>
                                                {msg._text || msg.parts?.[0]?.text}
                                            </Text>
                                        )}

                                        {/* Loading indicator */}
                                        {msg.pending && !msg._text && (
                                            <LoadingOutlined style={{ color: '#4285f4', fontSize: 14 }} spin />
                                        )}
                                    </div>

                                    {msg.role === 'user' && (
                                        <div style={{
                                            width: 24,
                                            height: 24,
                                            borderRadius: '50%',
                                            background: 'rgba(255, 255, 255, 0.1)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                            marginTop: 2
                                        }}>
                                            <UserOutlined style={{ fontSize: 12, color: '#8b949e' }} />
                                        </div>
                                    )}
                                </div>
                            ))}
                            <div ref={chatEndRef} />
                        </div>

                        {/* Image attachment preview */}
                        {attachedImage && (
                            <div style={{
                                padding: '8px 12px',
                                borderTop: '1px solid #30363d',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8
                            }}>
                                <img
                                    src={attachedImage.preview}
                                    alt="attachment"
                                    style={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: 6,
                                        objectFit: 'cover',
                                        border: '1px solid #30363d'
                                    }}
                                />
                                <Text style={{ color: '#8b949e', fontSize: 12, flex: 1 }} ellipsis>
                                    {attachedImage.name}
                                </Text>
                                <Button
                                    type="text"
                                    icon={<CloseOutlined />}
                                    size="small"
                                    onClick={() => setAttachedImage(null)}
                                    style={{ color: '#8b949e' }}
                                />
                            </div>
                        )}

                        {/* Input Area */}
                        <div style={{
                            padding: 12,
                            borderTop: '1px solid #30363d',
                        }}>
                            <div style={{ position: 'relative', display: 'flex', alignItems: 'flex-end', gap: 8 }}>
                                <Tooltip title="Attach image">
                                    <Button
                                        type="text"
                                        icon={<PaperClipOutlined />}
                                        size="small"
                                        onClick={() => fileInputRef.current?.click()}
                                        style={{
                                            color: attachedImage ? '#4285f4' : '#8b949e',
                                            marginBottom: 2
                                        }}
                                        disabled={streaming}
                                    />
                                </Tooltip>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/png,image/jpeg,image/webp,image/heic,image/heif"
                                    style={{ display: 'none' }}
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) handleImageAttach(file);
                                        e.target.value = ''; // Reset so same file can be re-selected
                                    }}
                                />
                                <TextArea
                                    placeholder="Message Gemini..."
                                    autoSize={{ minRows: 1, maxRows: 4 }}
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            handleSend();
                                        }
                                    }}
                                    disabled={streaming}
                                    style={{
                                        background: '#0d1117',
                                        border: '1px solid #30363d',
                                        borderRadius: 8,
                                        color: '#fff',
                                        resize: 'none',
                                        flex: 1
                                    }}
                                />
                                <Button
                                    type="primary"
                                    shape="circle"
                                    size="small"
                                    icon={streaming ? <LoadingOutlined /> : <SendOutlined />}
                                    onClick={handleSend}
                                    disabled={streaming || (!input.trim() && !attachedImage)}
                                    style={{ marginBottom: 2 }}
                                />
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default GeminiChatPanel;
